/**
 * Providers Context Composer
 * ---------------------------------------------------------------------------
 * Acts as the global context container for the application.
 *
 * ARCHITECTURE (THE DEPENDENCY CASCADE):
 * The order of these providers is CRITICAL. Data flows downwards.
 * 1. ConfigProvider: Fetches global settings (blocking load).
 * 2. AuthProvider: Identifies the user (depends on Firebase).
 * 3. ThemeProvider: Sets CSS tokens based on preferences/role.
 * 4. SidebarProvider: Global UI state for navigation.
 * 5. AlertProvider: Global system messaging.
 * 6. DialogProvider: Global modal management.
 * 7. MailboxProvider: Real-time system inbox.
 */

import type { ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import { ConfigProvider } from './ConfigContext';
import { AuthProvider } from './AuthContext';
import { ThemeProvider } from './ThemeContext';
import { SidebarProvider } from './SidebarContext';
import { AlertProvider } from './AlertContext';
import { DialogProvider } from './DialogContext';
import { MailboxProvider } from './MailboxContext';
import { PageTitleProvider } from './HelmetContext';

import { EmailTemplateProvider } from './EmailTemplateContext';
import ChatWidget from '../chat/ChatWidget';

export const Providers = ({ children }: { children: ReactNode }) => {
	return (
		<ConfigProvider>
			<AuthProvider>
				<EmailTemplateProvider>
					<ThemeProvider>
						<>
							<SidebarProvider>
								<AlertProvider>
									<DialogProvider>
										<MailboxProvider>
											<HelmetProvider>
												<PageTitleProvider>
													<BrowserRouter>
														{children}
														<ChatWidget />
													</BrowserRouter>
												</PageTitleProvider>
											</HelmetProvider>
										</MailboxProvider>
									</DialogProvider>
								</AlertProvider>
							</SidebarProvider>
						</>
					</ThemeProvider>
				</EmailTemplateProvider>
			</AuthProvider>
		</ConfigProvider>
	);
};
